import Yup from "yup";

import * as configUtils from "util/config";
import { areFormikRangeDatesComplete } from "util/form";
import * as i18n from "util/i18n";
import { getTransferUxLabel } from "util/transferInternalTitleLabels";

const FORM_ID = "transfers.historic";

const TRANSFER_HISTORIC_INTERNAL_LABEL_FALLBACKS = {
    internal: "A mis cuentas",
    "internal.tad": "Diferida a mis cuentas",
};

export function getTransferHistoricLabel(key) {
    const prefix = `${FORM_ID}.`;
    if (key.startsWith(prefix)) {
        const suffix = key.slice(prefix.length);
        if (suffix in TRANSFER_HISTORIC_INTERNAL_LABEL_FALLBACKS) {
            return getTransferUxLabel(key, TRANSFER_HISTORIC_INTERNAL_LABEL_FALLBACKS[suffix]);
        }
    }
    return i18n.get(key);
}

function parseAmount(value) {
    if (typeof value === "string") {
        return parseFloat(value.replaceAll(".", "").replace(",", "."));
    }
    return value;
}

export function getTransferHistoricConfig() {
    return {
        maxMonths: configUtils.getInteger("frontend.Transfers.maxMonths.ToSearch", 3),
        maxMonthsSchedule: configUtils.getInteger("frontend.scheduledTransactions.allowedPeriod.Months", 36),
        mobileSignatureControlTooltip: configUtils.get("frontend.mobileSignatureControl.tooltip", ""),
    };
}

export function buildTransferTypeOptions(getLabel = getTransferHistoricLabel) {
    return [
        {
            label: getLabel(`${FORM_ID}.all`),
            value: "all",
        },
        {
            label: getLabel(`${FORM_ID}.internal`),
            value: "transfers.internal.send",
        },
        {
            label: getLabel(`${FORM_ID}.internal.tad`),
            value: "transfers.internal.tad.send",
        },
        {
            label: getLabel(`${FORM_ID}.thirdParties`),
            value: "transfers.thirdParties.send",
        },
        {
            label: getLabel(`${FORM_ID}.thirdParties.tad`),
            value: "transfers.thirdParties.tad.send",
        },
        {
            label: getLabel(`${FORM_ID}.toContact`),
            value: "transfers.toContact.send",
        },
        {
            label: getLabel(`${FORM_ID}.toContact.tad`),
            value: "transfers.toContact.tad.send",
        },
        {
            label: getLabel(`${FORM_ID}.toContactCVU`),
            value: "transfersCVU.toContact.send",
        },
        {
            label: getLabel(`${FORM_ID}.thirdPartiesCVU`),
            value: "transfersCVU.thirdParties.send",
        },
    ];
}

export function buildTransferHistoricValidationSchema(getLabel) {
    return Yup.lazy((values) =>
        Yup.object().shape({
            beneficiary: Yup.string()
                .min(3, getLabel(`${FORM_ID}.validation.length`))
                .matches(/^[^[\]+?:\-_&*@#{}$()/";]+$/, getLabel(`${FORM_ID}.validation.format`)),
            cbuCvu: Yup.string().test(
                "is-valid-credit-account",
                getLabel("transfers.validation.invalidFormat"),
                (v) => {
                    if (!v) {
                        return true;
                    }
                    if (/^\d{22}$/.test(v)) {
                        return true;
                    }
                    return false;
                },
            ),
            reference: Yup.string()
                .min(3, getLabel(`${FORM_ID}.validation.length`))
                .matches(/^[^[\]+?:.\-_&*@#{}$()/";]+$/, getLabel(`${FORM_ID}.validation.format`)),
            minAmount: Yup.string().test(
                "minAmount",
                getLabel(`${FORM_ID}.validation.minAmount`),
                (minAmount) => parseAmount(minAmount) <= parseAmount(values.maxAmount),
            ),
            maxAmount: Yup.string().test(
                "maxAmount",
                getLabel(`${FORM_ID}.validation.maxAmount`),
                (maxAmount) => parseAmount(maxAmount) >= parseAmount(values.minAmount),
            ),
            dates: Yup.mixed().test("dates", getLabel(`${FORM_ID}.validation.range.date`), (dates) =>
                areFormikRangeDatesComplete(dates),
            ),
        }),
    );
}
